import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { lastValueFrom } from 'rxjs';
import { IdentitySecurityService } from 'src/app/services/identitySecurity';
import { LoggingService } from 'src/app/services/logging.service';

@Component({
  selector: 'app-enable-security-key-dialog',
  templateUrl: './enable-security-key-dialog.component.html'
})
export class EnableSecurityKeyDialogComponent implements OnInit {

  @Input('step') step!: number;
  @Input('userGuid') userId!: string;
  @Input('challenge') challenge!: any;
  securityKeyAdded: boolean = false;
  
  
  constructor(public activeModal: NgbActiveModal, private securityService: IdentitySecurityService, private loggingService: LoggingService) {
    // Must not be empty. It's approved by a comment.
  }

  async ngOnInit(): Promise<void> {
    this.register().catch((error) => this.loggingService.logException(error));
  }

  async close(){
    this.activeModal.close();
  }

  async register() {
    if (this.challenge.status !== "ok") {
      console.error("Error creating credential options");
      console.error(this.challenge.errorMessage);
      return;
    }

    // Turn the challenge back into the accepted format of padded base64
    this.challenge.challenge = this.coerceToArrayBuffer(this.challenge.challenge);
    // Turn ID into a UInt8Array Buffer for some reason
    this.challenge.user.id = this.coerceToArrayBuffer(this.challenge.user.id);

    this.challenge.excludeCredentials = this.challenge.excludeCredentials.map((c:any) => {
        c.id = this.coerceToArrayBuffer(c.id);
        return c;
    });

    if (this.challenge.authenticatorSelection.authenticatorAttachment === null) this.challenge.authenticatorSelection.authenticatorAttachment = undefined;

    let newCredential: any;
    try {
      newCredential = await navigator.credentials.create({
          publicKey: this.challenge
      });
    } catch (e) {
      let msg = "Could not create credentials in browser. Probably because the username is already registered with your authenticator. Please change username or authenticator."
      console.error(msg, e);
      return;
    }

    try {
      await this.registerNewCredential(newCredential);
    } catch (err: any) {
      console.error(err.message ? err.message : err);
      return;
    }
  }

  // This should be used to verify the auth data with the server
  registerNewCredential(newCredential: any) {
    // Move data into Arrays incase it is super long
    let attestationObject = new Uint8Array(newCredential.response.attestationObject);
    let clientDataJSON = new Uint8Array(newCredential.response.clientDataJSON);
    let rawId = new Uint8Array(newCredential.rawId);
    let id = new Uint8Array(newCredential.id);

    const data = {
      id: newCredential.id,
      rawId: this.coerceToBase64Url(rawId),
      type: newCredential.type,
      extensions: newCredential.getClientExtensionResults(),
      response: {
          attestationObject: this.coerceToBase64Url(attestationObject),
          clientDataJSON: this.coerceToBase64Url(clientDataJSON)
      }
    };

    try {
      this.registerCredentialWithServer(data);
    } catch (e) {
      console.error(e);
      return;
    }
  }

  registerCredentialWithServer(formData:any) {
    this.securityService.completeSecurityKeyRegistration(this.userId, formData)
      .then(value => {
        lastValueFrom(value)
          .then((data:any) => this.step = 2)
          .catch((error) => {
            this.loggingService.logException(error);
            this.step = 1;
          });
      })
      .catch((error) => {
        this.loggingService.logException(error);
        this.step = 1;
      });
  }

  private coerceToArrayBuffer(thing: any) {
    if (typeof thing === "string") {
        // base64url to base64
        thing = thing.replace(/-/g, "+").replace(/_/g, "/");

        // base64 to Uint8Array
        let str = window.atob(thing);
        let bytes = new Uint8Array(str.length);
        for (let i = 0; i < str.length; i++) {
            bytes[i] = str.charCodeAt(i);
        }
        thing = bytes;
    }

    // Array to Uint8Array
    if (Array.isArray(thing)) {
        thing = new Uint8Array(thing);
    }

    // Uint8Array to ArrayBuffer
    if (thing instanceof Uint8Array) {
        thing = thing.buffer;
    }

    // error if none of the above worked
    if (!(thing instanceof ArrayBuffer)) {
        throw new TypeError("could not coerce to ArrayBuffer");
    }

    return thing;
  };

  coerceToBase64Url(thing: any) {
    // Array or ArrayBuffer to Uint8Array
    if (Array.isArray(thing)) {
        thing = Uint8Array.from(thing);
    }

    if (thing instanceof ArrayBuffer) {
        thing = new Uint8Array(thing);
    }

    // Uint8Array to base64
    if (thing instanceof Uint8Array) {
        let str = "";
        let len = thing.byteLength;

        for (let i = 0; i < len; i++) {
            str += String.fromCharCode(thing[i]);
        }
        thing = window.btoa(str);
    }

    if (typeof thing !== "string") {
        throw new Error("could not coerce to string");
    }

    // base64 to base64url
    // NOTE: "=" at the end of challenge is optional, strip it off here
    thing = thing.replace(/\+/g, "-").replace(/\//g, "_").replace(/=*$/g, "");

    return thing;
};


}
